import React, { PureComponent } from 'react';
import {
    MapLayer,
    MapLayerCustomAction,
    MapLayerCustomProperties,
    MapLayerFilter,
    MapLayerParameters,
    MapLayerType,
    MapLayerUtils,
    MapObjectType,
    ObjectToDisplay,
} from 'app/components/dashboard/network/map/Map';
import {
    LoadMapTrafficVolume,
    MapTrafficVolumeCacheType,
    MapTrafficVolumeCacheUpdated,
} from 'app/components/dashboard/network/map/MapTrafficVolumeCache';
import {
    ClearMapLayerEvent,
    DeleteCustomLayerEvent,
    LayerUpdateEvent,
    ShowMapLayerEvent,
} from 'app/common/eventbus/Events';
import memoizeOne from 'memoize-one';
import { FlightLevelBand, HotspotId, OTSRoute, RegulationOrMcdmOnlyDto, TimePeriod } from 'app/model/CORE';
import {
    AccMap,
    HotspotMap,
    MapMeasureImpact,
    MonitorableMapMeasure,
    MonitorableMapTv,
    RegulationOutlookMap,
    TvImpactMap,
} from 'app/components/map/util/MapUtils';
import { ExtendedMonitoredTv, MilitaryZoneDto } from 'app/model/SOL49';
import Utils from 'app/common/util/Utils';
import { Collapse, CollapsePanelProps, Popconfirm } from 'antd';
import {
    DeleteOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    FontSizeOutlined,
    SettingOutlined,
} from '@ant-design/icons/lib';
import { Flex } from 'app/components/common/Flex';
import omit from 'lodash/omit';
import { ClosablePopover } from 'app/components/common/ClosablePopover';
import {
    MapLayerSettingsPopupContent,
    MapLayerSettingsPopupTitle,
} from 'app/components/dashboard/network/map/MapLayerSettingsPopup';
import AppContext from 'app/common/context/AppContext';
import difference from 'lodash/difference';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { MapConvection } from 'app/components/map/CoreMap';
import { PlayCircleOutlined } from '@ant-design/icons';
import { RootState } from 'app/redux/store';
import { connect, ConnectedProps } from 'react-redux';

const { Panel } = Collapse;

const mapStateToProps = (state: RootState, ownProps) => {
    return {
        tvCache: state.mapFullscreenData.tvCache,
    };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface LayerSelectionEntryProps extends Omit<CollapsePanelProps, 'id'>, PropsFromRedux {
    //because this component wraps a Collapse.Panel and they need to be directly into the Collapse
    timePeriod: TimePeriod;
    flightLevelBand: FlightLevelBand;
    //TODO improve this, is a pain having to copy over the props from the MapLayer
    //just copy pasted the MapLayer properties in here because I wanted a more fined grained property update selection (instead of digging into an object)
    id: number;
    objectsToDisplay: Array<ObjectToDisplay>;
    type: MapLayerType;
    mapType: Array<MapObjectType>;
    active?: boolean;
    showLabel?: boolean;
    filters?: Array<MapLayerFilter>;
    priority: number;
    group?: string;
    selection: Array<ObjectToDisplay>;
    label: string;
    customProperties?: MapLayerCustomProperties;
    customActions?: { [key: string]: MapLayerCustomAction };
    parameters?: MapLayerParameters;
    mapId: number;
}

const ALL_FILTER_ID: number = -1;

class LayerSelectionEntry extends React.PureComponent<LayerSelectionEntryProps> {
    private settingsPopoverRef = React.createRef<ClosablePopover>();

    constructor(props) {
        super(props);
        this.initBus();
    }

    initBus() {
        AppContext.eventBus.register(MapTrafficVolumeCacheUpdated.NAME, (event: MapTrafficVolumeCacheUpdated) => {
            if (this.hasRelevantCacheUpdates(event.previousCache, event.cache) && this.props.active) {
                //only refresh map if the new cache value has relevant data for the layer
                this.refreshMap(event.cache);
            }
        });
    }

    private hasRelevantCacheUpdates(prevCache: MapTrafficVolumeCacheType, cache: MapTrafficVolumeCacheType) {
        const layerTvIds = MapLayerUtils.getLayerObjectsToDisplayTvIds(this.toMapLayer(this.props));
        const prevCacheTvIds = Object.keys(prevCache).filter(tvId => layerTvIds.includes(tvId));
        const cacheTvIds = Object.keys(cache).filter(tvId => layerTvIds.includes(tvId));
        return difference(cacheTvIds, prevCacheTvIds).length > 0;
    }

    componentDidMount(): void {
        //added mostly because when the user "hides" the map is getting UNMOUNT
        this.componentDidUpdate(null, this.props);
    }

    componentDidUpdate(prevProps: Readonly<LayerSelectionEntryProps>, prevState: Readonly<{}>, snapshot?: any): void {
        //active flag change
        if (prevProps?.active !== this.props.active && !this.props.active) {
            //become inactive
            ClearMapLayerEvent.fire(this.props.mapId, this.toMapLayer(this.props));
            return;
        }
        if (
            this.props.active &&
            (!Utils.isEquals(this.toMapLayer(this.props), this.toMapLayer(prevProps)) ||
                Utils.isNotEquals(this.props.timePeriod, prevProps?.timePeriod) ||
                Utils.isNotEquals(this.props.flightLevelBand, prevProps?.flightLevelBand) ||
                prevProps.mapId !== this.props.mapId || //if the target map changes (gets un mounted) it needs to re-display
                Utils.isNotEquals(prevProps.customProperties, this.props.customProperties)) //some custom properties are used in the render
        ) {
            this.refreshMap(this.props.tvCache);
        }
    }

    refreshMap(cache: MapTrafficVolumeCacheType) {
        const { timePeriod, objectsToDisplay, selection, flightLevelBand } = this.props;
        LayerSelectionEntry.retrievesRequiredDefinitions(cache, objectsToDisplay); //load definitions if needed to
        //TODO prevent sending the event when there is still objects definition to be loaded?
        const mapObjects = this.getObjectsToDisplay(objectsToDisplay, selection, cache);
        ShowMapLayerEvent.fire(this.props.mapId, this.toMapLayer(this.props), mapObjects, timePeriod, flightLevelBand);
    }

    toMapLayer(value: LayerSelectionEntryProps): MapLayer {
        if (value) {
            return {
                id: value.id,
                objectsToDisplay: value.objectsToDisplay,
                type: value.type,
                mapType: value.mapType,
                active: value.active,
                showLabel: value.showLabel,
                filters: value.filters,
                priority: value.priority,
                group: value.group,
                selection: value.selection,
                label: value.label,
                customProperties: value.customProperties,
                parameters: value.parameters,
                customActions: value.customActions,
            };
        } else {
            return null;
        }
    }

    static retrievesRequiredDefinitions(
        cache: MapTrafficVolumeCacheType,
        objectsToDisplay: Array<ObjectToDisplay>,
    ): boolean {
        const cachedTvIds = Object.keys(cache);
        const missingTvs = MapLayerUtils.getObjectsToDisplayTvIds(objectsToDisplay).filter(
            tvId => !cachedTvIds.includes(tvId),
        );
        if (missingTvs?.length > 0) {
            console.debug('LoadMapTV for', missingTvs);
            LoadMapTrafficVolume.fire(missingTvs);
            return true;
        }
        return false;
    }

    getObjectsToDisplay(
        objects: Array<ObjectToDisplay>,
        selection: Array<ObjectToDisplay>,
        cache: MapTrafficVolumeCacheType,
    ) {
        if (objects) {
            const filteredData = this.hasFilters() ? this.getFilteredData(objects) : objects;
            const selectedMeasureIds = selection
                .filter(obj => obj.type === MapObjectType.MEASURE)
                .map(obj => MapLayerUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(obj.object));
            const selectedTvIds = selection
                .filter(obj => obj.type === MapObjectType.TV)
                .map(obj => MapLayerUtils.EXTENDED_MONITORED_TV_KEY_PROVIDER.getKey(obj.object));
            const selectedHotspots = selection
                .filter(obj => obj.type === MapObjectType.HOTSPOT)
                .map(obj => MapLayerUtils.EXTENDED_MONITORED_TV_KEY_PROVIDER.getKey(obj.object));
            const selectedRegOutlook = selection
                .filter(obj => obj.type === MapObjectType.REGULATION_OUTLOOK)
                .map(obj => MapLayerUtils.MONITORED_REG_OUTLOOK_ID_PROVIDER.getKey(obj.object));
            return filteredData
                .filter(
                    obj =>
                        Utils.isInList(
                            obj.type,
                            MapObjectType.TV,
                            MapObjectType.HOTSPOT,
                            MapObjectType.REGULATION_OUTLOOK,
                            MapObjectType.CONVECTION,
                            MapObjectType.ACC,
                            MapObjectType.MILITARY,
                            MapObjectType.NAT_TRACK,
                            MapObjectType.IMPACTED_TV,
                            MapObjectType.IMPACTED_MEASURE,
                        ) ||
                        (Utils.isInList(obj.type, MapObjectType.MEASURE) &&
                            MapLayerUtils.isValidMapMeasure(obj.object)),
                )
                .map((obj, index) => {
                    switch (obj.type) {
                        case MapObjectType.TV:
                            return {
                                tv: obj.object,
                                index: index,
                                mapTv: cache[(obj.object as ExtendedMonitoredTv).refLocation.id],
                                selected: selectedTvIds.includes(
                                    MapLayerUtils.EXTENDED_MONITORED_TV_KEY_PROVIDER.getKey(obj.object),
                                ),
                            } as MonitorableMapTv;
                        case MapObjectType.MEASURE:
                            return {
                                measure: obj.object,
                                index: index,
                                mapTv: cache[(obj.object['measure'] as RegulationOrMcdmOnlyDto).location.id],
                                selected: selectedMeasureIds.includes(
                                    MapLayerUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(obj.object),
                                ),
                            } as MonitorableMapMeasure;
                        case MapObjectType.HOTSPOT:
                            return {
                                hotspot: obj.object,
                                index: index,
                                mapTv: cache[(obj.object['hotspotId'] as HotspotId).tvId],
                                selected: selectedHotspots.includes((obj.object['hotspotId'] as HotspotId).tvId),
                            } as HotspotMap;
                        case MapObjectType.REGULATION_OUTLOOK:
                            return {
                                outlook: obj.object['regulationOutlook'],
                                index: index,
                                selected: selectedRegOutlook.includes(
                                    MapLayerUtils.MONITORED_REG_OUTLOOK_ID_PROVIDER.getKey(obj.object),
                                ),
                            } as RegulationOutlookMap;
                        case MapObjectType.CONVECTION:
                            return obj.object as MapConvection;
                        case MapObjectType.ACC:
                            return { acc: obj.object, selected: false } as AccMap; //TODO requires selection?
                        case MapObjectType.MILITARY:
                            return obj.object as MilitaryZoneDto; //TODO requires selection?
                        case MapObjectType.NAT_TRACK:
                            return obj.object as OTSRoute; //TODO requires selection?
                        case MapObjectType.IMPACTED_TV:
                            return {
                                impact: obj.object,
                                mapTv: cache[MapLayerUtils.IMPACTED_TV_TV_ID_PROVIDER.getKey(obj.object)],
                            } as TvImpactMap;
                        case MapObjectType.IMPACTED_MEASURE:
                            return {
                                measure: obj.object,
                                mapTv: cache[MapLayerUtils.IMPACTED_MEASURE_TV_ID_PROVIDER.getKey(obj.object)],
                            } as MapMeasureImpact;
                        default:
                            return null; //should never happen
                    }
                });
        }
        return [];
    }

    hasFilters() {
        return this.props.filters?.length > 0 && this.props.filters.filter(filter => filter.active).length > 0;
    }

    getFilteredData(objects: Array<ObjectToDisplay>): Array<ObjectToDisplay> {
        const activeFilters = this.props.filters?.filter(filter => filter.active);
        //sanity check: only filters if there is active filters
        if (activeFilters?.length > 0) {
            const result = [];
            for (const filter of activeFilters) {
                result.push(
                    ...objects.filter(obj => {
                        if (typeof filter.filterKeys === 'function') {
                            return filter.filterKeys(obj, filter);
                        } else {
                            const objectKey =
                                obj.type === MapObjectType.MEASURE
                                    ? MapLayerUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(obj.object)
                                    : MapLayerUtils.EXTENDED_MONITORED_TV_KEY_PROVIDER.getKey(obj.object);
                            return filter.filterKeys.includes(objectKey);
                        }
                    }),
                );
            }
            return result;
        } else {
            return objects;
        }
    }

    getPanelProps() {
        return omit(this.props, [
            'cache',
            'timePeriod',
            'countType',
            'id',
            'objectsToDisplay',
            'type',
            'mapType',
            'active',
            'showLabel',
            'filters',
            'priority',
            'group',
            'selection',
            'label',
            'customProperties',
            'flightLevelBand',
            'customActions',
            'parameters',
            'mapId',
            'tvCache',
        ]);
    }

    render() {
        const panelProps = this.getPanelProps();
        return (
            <Panel {...panelProps} key={this.props.id} header={this.renderHeader()}>
                {this.renderFilters()}
                {this.renderCustomActions()}
            </Panel>
        );
    }

    renderHeader() {
        return (
            <Flex direction={'horizontal'} size={'15em'}>
                <Flex style={{ justifyContent: 'left' }}>
                    <LayerEntryIcon
                        label={this.props.label}
                        active={this.props.active}
                        id={this.props.id}
                        labelActive={this.props.showLabel}
                        onActiveClick={(event, id) => {
                            // event.stopPropagation();
                            this.handleActiveChange();
                        }}
                        onLabelActiveClick={(event, id) => {
                            // event.stopPropagation();
                            this.handleShowLabelChange();
                        }}
                    />
                </Flex>
                <ConditionalRender condition={this.props.type === MapLayerType.CUSTOM}>
                    <Flex
                        className={'iconOnSpan'}
                        size={'1.5em'}
                        style={{ fontSize: '1em', justifyContent: 'flex-end' }}>
                        <Popconfirm
                            title={'Do you want to delete the layer ?'}
                            okText={'Yes'}
                            cancelText={'No'}
                            onConfirm={this.handleDeleteCustomLayer}>
                            <DeleteOutlined />
                        </Popconfirm>
                    </Flex>
                </ConditionalRender>
                <Flex className={'iconOnSpan'} size={'1.5em'} style={{ fontSize: '1em', justifyContent: 'flex-end' }}>
                    {this.renderMapLayerSettings()}
                </Flex>
            </Flex>
        );
    }

    renderFilters() {
        if (this.props.filters?.length) {
            return (
                <Flex key={`${this.props.id}-filters`} direction="vertical" style={{ marginLeft: '3.5em' }}>
                    <Flex key={`${this.props.id}-filters-all`}>
                        <LayerEntryIcon
                            id={ALL_FILTER_ID}
                            active={this.isNoFiltersActive(this.props.filters)}
                            label="All"
                            onActiveClick={this.handleFilterOnClick}
                        />
                    </Flex>
                    <Flex direction="vertical">
                        {this.props.filters.map(filter => {
                            return (
                                <LayerEntryIcon
                                    id={filter.id}
                                    key={`${this.props.id}-filters-${filter.id}`}
                                    active={filter.active}
                                    label={filter.name}
                                    onActiveClick={this.handleFilterOnClick}
                                />
                            );
                        })}
                    </Flex>
                </Flex>
            );
        }
    }

    renderMapLayerSettings() {
        return (
            <span className="detailed-subpanel-header-right">
                <ClosablePopover
                    key="map-settings-action"
                    ref={this.settingsPopoverRef}
                    trigger="click"
                    placement="bottom"
                    title={<MapLayerSettingsPopupTitle />}
                    content={
                        <MapLayerSettingsPopupContent
                            settings={this.toMapLayer(this.props)}
                            onChange={this.handleSettingsChange}
                            onClose={() => this.settingsPopoverRef.current.hide()}
                        />
                    }>
                    <SettingOutlined onClick={() => this.settingsPopoverRef.current.show()} />
                </ClosablePopover>
            </span>
        );
    }

    renderCustomActions() {
        if (this.props.customActions && Object.keys(this.props.customActions)?.length > 0) {
            return (
                <Flex key={`${this.props.id}-customActions`} direction="vertical" style={{ marginLeft: '3.5em' }}>
                    {Object.keys(this.props.customActions).map(actionLabel => {
                        return (
                            <Flex key={`${this.props.id}-customAction-${actionLabel}`}>
                                <span
                                    title={actionLabel}
                                    className="iconOnSpan"
                                    key={`${this.props.id}-customAction-body-${actionLabel}`}
                                    onClick={() => this.handleMapLayerCustomAction(actionLabel)}>
                                    {Utils.isExists(this.props.customActions[actionLabel].icon) ? (
                                        React.cloneElement(this.props.customActions[actionLabel].icon, {
                                            key: `${this.props.id}-customAction-${actionLabel}-icon`,
                                            style: { marginRight: '0.5em' },
                                        })
                                    ) : (
                                        <PlayCircleOutlined
                                            key={`${this.props.id}-customAction-${actionLabel}-icon`}
                                            style={{ marginRight: '0.5em' }}
                                        />
                                    )}
                                    {actionLabel}
                                </span>
                            </Flex>
                        );
                    })}
                </Flex>
            );
        }
    }

    isNoFiltersActive = memoizeOne((filters: Array<MapLayerFilter>) => {
        return filters.filter(filter => filter.active).length === 0;
    });

    handleSettingsChange = (mapLayer: MapLayer) => {
        this.settingsPopoverRef.current.hide();
        LayerUpdateEvent.fire(this.props.mapId, mapLayer);
    };

    handleFilterOnClick = (event, filterId: number): void => {
        let updatedLayer: MapLayer;
        if (filterId === ALL_FILTER_ID) {
            const newActiveValue = this.isNoFiltersActive(this.props.filters);
            const newFilters: Array<MapLayerFilter> = this.props.filters.map((filter: MapLayerFilter) => {
                return Object.assign({}, filter, { active: newActiveValue });
            });
            updatedLayer = Object.assign({}, this.toMapLayer(this.props), { filters: newFilters });
        } else {
            const newFilters: Array<MapLayerFilter> = this.props.filters.map((filter: MapLayerFilter) => {
                if (filter.id === filterId) {
                    return Object.assign({}, filter, { active: !filter.active });
                } else {
                    return filter;
                }
            });
            updatedLayer = Object.assign({}, this.toMapLayer(this.props), { filters: newFilters });
        }

        if (updatedLayer) {
            LayerUpdateEvent.fire(this.props.mapId, updatedLayer);
        }
    };

    handleActiveChange = () => {
        const updatedLayer: MapLayer = Object.assign({}, this.toMapLayer(this.props), { active: !this.props.active });
        LayerUpdateEvent.fire(this.props.mapId, updatedLayer);
    };

    handleShowLabelChange = () => {
        const updatedLayer: MapLayer = Object.assign({}, this.toMapLayer(this.props), {
            showLabel: !this.props.showLabel,
        });
        LayerUpdateEvent.fire(this.props.mapId, updatedLayer);
    };

    handleDeleteCustomLayer = () => {
        DeleteCustomLayerEvent.fire(this.props);
    };

    handleMapLayerCustomAction(action: string) {
        if (this.props.customActions[action]) {
            this.props.customActions[action].handler(this.toMapLayer(this.props));
        }
    }
}

interface LayerEntryIconProps {
    id: number;
    active: boolean;
    labelActive?: boolean;
    label: string;

    onActiveClick?(event, id: number): void;

    onLabelActiveClick?(event, id: number): void;
}

class LayerEntryIcon extends PureComponent<LayerEntryIconProps> {
    render() {
        return (
            <Flex direction="horizontal">
                <span title="Toggle visibility" className={'iconOnSpan'} onClick={this.handleOnActiveClick}>
                    {this.props.active ? (
                        <EyeOutlined
                            style={{ marginRight: '1em' }}
                            className={this.props.active ? 'iconSelected' : undefined}
                        />
                    ) : (
                        <EyeInvisibleOutlined style={{ marginRight: '1em' }} />
                    )}
                </span>
                <ConditionalRender condition={Utils.isExists(this.props.labelActive)}>
                    <span className={'iconOnSpan'} onClick={this.handleOnLabelActiveClick}>
                        {/*The icon isn't the best one so suggestions are welcome*/}
                        <FontSizeOutlined
                            title={'Toggle label'}
                            style={{ marginRight: '1em' }}
                            className={this.props.labelActive ? 'iconSelected' : undefined}
                        />
                    </span>
                </ConditionalRender>
                <span>{this.props.label}</span>
            </Flex>
        );
    }

    handleOnActiveClick = event => {
        if (this.props.onActiveClick) {
            this.props.onActiveClick(event, this.props.id);
        }
    };

    handleOnLabelActiveClick = event => {
        if (this.props.onLabelActiveClick) {
            this.props.onLabelActiveClick(event, this.props.id);
        }
    };
}

export default connector(LayerSelectionEntry);
